import React, { useState, useEffect } from 'react';
import ImageView from 'Components/Image/ImageView';
import { getNameInitials, getUserImage, humanize, titleize } from 'helpers';
import { FiCalendar, FiTag, FiUserPlus } from 'react-icons/fi';
import CardStyles from './KanbanCard.module.scss';
import KanbanDropDown from './KanbanDropDown';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'constants/routes';
import NewDocumentModal from 'Components/Modals/NewDocumentModal/NewDocumentModal';
import { ReactComponent as Other } from '../../../src/assets/svg/Group.svg';
import { CONTENT_TYPE_OPTIONS } from 'constants/contentTypes';
import moment from 'moment';
import UpgradeModal from 'Containers/Modal/UpgradeModal/UpgradeModal';
import { useSelector } from 'react-redux';

const getCardClass = (status) => {
  if (status === 'not_started') {
    return 'Card__NotStarted';
  }

  if (status === 'in_progress') {
    return 'Card__Inprogress';
  }

  if (status === 'in_review') {
    return 'Card__Inreview';
  }

  if (status === 'published') {
    return 'Card__Published';
  }

  if (status === 'approved') {
    return 'Card__Approved';
  }
};

const KanbanCard = ({
  status,
  title,
  id,
  document,
  onDelete,
  onMove,
  onEdit,
  onDuplicate,
  deadline,
  priority,
  assignedToImage,
  assignedTo,
  onDragStart,
  project_id,
  access = '',
  fetchCPTasks,
  state,
  onArchive,
  description,
  content_type,
  other_content_type,
  handleAICreate,
  showCheckbox = false,
  isSelected = false,
  onSelect,
}) => {
  const { template_id, id: documentId } = document || {};
  const [NewDocumentModalView, setNewDocumentModalView] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const { id: userId, subscriptions } = user || {};
  const { current_subscription: currentSubscription } = subscriptions ?? {};

  const navigate = useNavigate();

  const handleDraftLink = () => {
    if (template_id) {
      navigate(ROUTES.EditorDocumentTemplate(documentId, template_id));
    } else {
      navigate(ROUTES.FreestyleDocumentTemplate(documentId));
    }
  };

  const onHideModal = (refetchTasks = false) => {
    refetchTasks && fetchCPTasks();
    setNewDocumentModalView(false);
  };

  const findIcon = (content_type) => {
    const option = CONTENT_TYPE_OPTIONS.find(
      (opt) => opt.value === content_type,
    );
    return option?.icon || <Other />;
  };
  const icon = findIcon(content_type);

  return (
    <section
      className={`${CardStyles.Card} ${getCardClass(status)} ${isSelected ? 'selected' : ''}`}
      onDragStart={(e) => onDragStart(e, id, project_id, documentId)}
      style={{ cursor: 'pointer' }}
      draggable
    >
      {/* Checkbox for bulk selection */}
      {showCheckbox && (
        <div
          className={`checkbox-container ${CardStyles.kanbanCheckbox}`}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => onSelect && onSelect(id, e.target.checked)}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
      <ul className="">
        {/* Content Type Section */}
        <li
          className={'d-flex flex-column ' + CardStyles.Card__CardWrapper}
          onClick={() => onEdit(id)}
        >
          {!!content_type && (
            <div className="d-flex justify-content-between align-items-center" style={{ padding: '0 8px' }}>
              <div
                className={
                  'd-flex align-items-center ' +
                  CardStyles.Card__CardWrapper_BottomSection
                }
                style={{ gap: '8px', flex: 1 }}
              >
                {icon}
                <span>
                  {content_type === 'other'
                    ? humanize(other_content_type)
                    : humanize(content_type || '')}
                </span>
              </div>
            </div>
          )}

          {/* Title and Date Section */}
          <div className="d-flex flex-column" style={{ gap: '8px', padding: '8px 8px 0 8px' }}>
            <div
              className="d-flex flex-column"
              style={{
                gap: '8px',
                paddingBottom: '12px',
                borderBottom: '1px solid #eaecf0',
              }}
            >
              <p className={CardStyles.Card__Title}>{title}</p>
              {deadline && (
                <div
                  className={
                    'd-flex align-items-center ' +
                    CardStyles.Card__CardWrapper_BottomSection
                  }
                  style={{ gap: '8px' }}
                >
                  <FiCalendar color="#475467" size={16} />
                  <span>
                    {moment(deadline).format('MM/DD/YYYY')}
                  </span>
                </div>
              )}
            </div>
          </div>
        </li>

        {/* Footer Section */}
        <li
          className={
            'd-flex flex-column border-0 ' +
            CardStyles.Card__CardWrapper__footer
          }
        >
          <div className="d-flex justify-content-between align-items-center">
            <div className={CardStyles.card_footer_name_section}>
              {!assignedTo ? (
                <div className={CardStyles.card_footer_name_section__avatar}>
                  <FiUserPlus size={16} />
                </div>
              ) : assignedToImage ? (
                <div className={CardStyles.card_footer_name_section__image}>
                  <ImageView
                    className={CardStyles.card_footer_name_section__image__img}
                    src={assignedToImage}
                  />
                </div>
              ) : (
                <div
                  className={CardStyles.card_footer_name_section__userInitials}
                >
                  {getNameInitials(assignedTo)}
                </div>
              )}
              <p className={CardStyles.card_footer_name_section__name}>
                {assignedTo ?? 'Unassigned'}
              </p>
            </div>
            <div className="d-flex gap-2 align-items-center">
              {documentId ? (
                <div
                  className="d-flex align-items-center"
                  style={{ padding: '8px 14px', cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDraftLink();
                  }}
                >
                  <p className={CardStyles.link_card_draft}>View Content</p>
                </div>
              ) : (
                <div
                  className="d-flex align-items-center"
                  style={{ padding: '8px 14px', cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAICreate(id, project_id, title);
                  }}
                >
                  <span className={CardStyles.link_card_draft}>
                    View Content
                  </span>
                </div>
              )}
              <span onClick={(e) => e.stopPropagation()}>
                <KanbanDropDown
                  id={id}
                  onDelete={onDelete}
                  onMove={onMove}
                  onEdit={onEdit}
                  onDuplicate={onDuplicate}
                  access={access}
                  state={state}
                  onArchive={onArchive}
                  threedots={CardStyles.DropRotate}
                />
              </span>
            </div>
          </div>
        </li>
      </ul>
      {showUpgradeModal && (
        <UpgradeModal
          show={showUpgradeModal}
          onHide={() => setShowUpgradeModal(false)}
          message={
            'Updating campaign related content is a pro feature. Please update to pro plan to access this feature.'
          }
        />
      )}

      <NewDocumentModal
        show={NewDocumentModalView}
        onHide={(refetchTasks) => onHideModal(refetchTasks)}
        taskId={id}
        taskName={title}
        projectId={project_id}
        aiPowered
      />
    </section>
  );
};

export default KanbanCard;
